import { Controller } from "@hotwired/stimulus"

const MENU_ITEM_SELECTOR =
  '[role="menuitem"]:not([disabled]), a:not([disabled]), button:not([disabled])'

// Connects to data-controller="ui--dropdown"
// Handles dropdown menu show/hide with click outside detection and keyboard navigation
export default class extends Controller {
  static targets = ["menu", "trigger", "backdrop"]

  connect() {
    // Listen for escape key
    this.escapeHandler = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.escapeHandler)

    // Keyboard navigation within menu
    this._keydownHandler = this._handleMenuKeydown.bind(this)
    this.element.addEventListener("keydown", this._keydownHandler)
  }

  disconnect() {
    document.removeEventListener("keydown", this.escapeHandler)
    this.element.removeEventListener("keydown", this._keydownHandler)
  }

  toggle(event) {
    event.stopPropagation()

    if (this.isOpen()) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (this.hasMenuTarget && this.hasBackdropTarget) {
      this.menuTarget.classList.remove("hidden")
      this.backdropTarget.classList.remove("hidden")

      // Focus first menu item
      requestAnimationFrame(() => {
        const firstItem = this.menuTarget.querySelector(MENU_ITEM_SELECTOR)
        if (firstItem) {
          firstItem.focus()
        }
      })

      // Dispatch open event
      this.element.dispatchEvent(
        new CustomEvent("dropdown:open", { bubbles: true }),
      )
    }
  }

  close() {
    if (this.hasMenuTarget && this.hasBackdropTarget) {
      this.menuTarget.classList.add("hidden")
      this.backdropTarget.classList.add("hidden")

      // Return focus to trigger
      if (this.hasTriggerTarget) {
        const focusable =
          this.triggerTarget.querySelector("button, a, [tabindex]") ||
          this.triggerTarget
        focusable.focus()
      }

      // Dispatch close event
      this.element.dispatchEvent(
        new CustomEvent("dropdown:close", { bubbles: true }),
      )
    }
  }

  handleEscape(event) {
    if (event.key === "Escape" && this.isOpen()) {
      this.close()
    }
  }

  _handleMenuKeydown(event) {
    if (!this.isOpen()) return

    const items = Array.from(
      this.menuTarget.querySelectorAll(MENU_ITEM_SELECTOR),
    )
    if (items.length === 0) return

    const currentIndex = items.indexOf(document.activeElement)

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault()
        const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
        items[nextIndex].focus()
        break
      }
      case "ArrowUp": {
        event.preventDefault()
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
        items[prevIndex].focus()
        break
      }
      case "Home": {
        event.preventDefault()
        items[0].focus()
        break
      }
      case "End": {
        event.preventDefault()
        items[items.length - 1].focus()
        break
      }
      case "Enter":
      case " ": {
        // Let the browser handle the click on the focused item
        if (document.activeElement && items.includes(document.activeElement)) {
          // Close after selection
          requestAnimationFrame(() => this.close())
        }
        break
      }
      case "Tab": {
        // Close menu when tabbing out
        this.close()
        break
      }
    }
  }

  isOpen() {
    return this.hasMenuTarget && !this.menuTarget.classList.contains("hidden")
  }
}
